/**
 * ISOLATED DATA LOADING ORCHESTRATOR
 * Coordinates isolated data loaders for each feature
 * Complete separation between features - no conflicts
 */

import { useEffect } from "react";
import Papa from "papaparse";
import { useFeature1DataLoader } from "./data/useFeature1DataLoader";
import { useFeature2DataLoader } from "./data/useFeature2DataLoader";
import { useFeature4DataLoader } from "./data/useFeature4DataLoader";
import { useIndexAndCrumbsLoader } from "./data/useIndexAndCrumbsLoader";
import { fetchR2CsvText } from "../utils/fetchR2Csv";

/**
 * Isolated Data Loading Hook
 * Orchestrates completely separate data loading for each feature
 */
export function useIsolatedDataLoading({
  // Feature 1 State
  feature1Data,
  setFeature1Data,
  feature1Loading,
  setFeature1Loading,
  feature1Error,
  setFeature1Error,

  // Feature 2 State
  feature2Data,
  setFeature2Data,
  feature2Loading,
  setFeature2Loading,
  feature2Error,
  setFeature2Error,

  // Feature 4 State
  feature4Data,
  setFeature4Data,
  feature4Loading,
  setFeature4Loading,
  feature4Error,
  setFeature4Error,

  // Shared State
  areaType,
  selectedName,
  setSelectedName,
  selectedMetro,
  setSelectedMetro,
  view,
  levelItems,
  setLevelItems,
  crumbsByName,
  setCrumbsByName,
  populationByCity,
  setPopulationByCity,
  rawCityNames,
  setRawCityNames,
  minPopOnly,
  setIsLoading,
  setCurrentChartMetric,
  // Links state for Explore
  linksByName,
  setLinksByName,
  linksByRegionId,
  setLinksByRegionId
}) {

  console.log(`[ISOLATED_LOADING] Orchestrating data loading for view: ${view}, areaType: ${areaType}`);

  // INDEX AND CRUMBS LOADER (Shared - Non-conflicting)
  console.log(`[ISOLATED_LOADING] About to call useIndexAndCrumbsLoader`);
  try {
    useIndexAndCrumbsLoader({
      areaType,
      minPopOnly,
      populationByCity,
      setPopulationByCity,
      setLevelItems,
      setSelectedName,
      setCrumbsByName,
      setRawCityNames,
      rawCityNames,
      levelItems,
      crumbsByName
    });
    console.log(`[ISOLATED_LOADING] useIndexAndCrumbsLoader completed successfully`);
  } catch (error) {
    console.error(`[ISOLATED_LOADING] Error in useIndexAndCrumbsLoader:`, error);
  }

  // FEATURE 1: Data Snapshot Loader (Isolated) - Load for all area types when not in Feature 2 or 4
  const isFeature2Or4 = view === "performance" || view === "performance-snapshot" || view === "newMarketsTP" || view === "newMarketsTP-snapshot";
  const shouldLoadFeature1 = areaType !== "" && !isFeature2Or4;
  console.log(`[🔥 FEATURE1_SHOULD_LOAD] Calculation:`, {
    areaType,
    areaTypeNotEmpty: areaType !== "",
    view,
    isFeature2Or4,
    notFeature2Or4: !isFeature2Or4,
    finalShouldLoad: shouldLoadFeature1
  });
  console.log(`[ISOLATED_LOADING] Feature 1 - Calling useFeature1DataLoader with:`, {
    areaType,
    selectedName,
    shouldLoad: shouldLoadFeature1
  });
  
  console.log(`[ISOLATED_LOADING] Feature 1 - About to call useFeature1DataLoader hook`);
  
  const feature1Loader = useFeature1DataLoader({
    areaType,
    selectedName,
    setFeature1Data,
    setFeature1Loading,
    setFeature1Error,
    shouldLoad: shouldLoadFeature1,
    feature1Data  // Pass data so loader can detect when it's cleared
  });
  
  console.log(`[ISOLATED_LOADING] Feature 1 - Hook call completed`);
  console.log(`[ISOLATED_LOADING] Feature 1 - Loader created:`, {
    hasLoader: !!feature1Loader,
    loaderMethods: feature1Loader ? Object.keys(feature1Loader) : []
  });
  
  // Debug if Feature 1 loader is being called
  useEffect(() => {
    console.log(`[ISOLATED_LOADING] Feature 1 loader effect - shouldLoad: ${shouldLoadFeature1}, areaType: ${areaType}, view: ${view}`);
    if (shouldLoadFeature1) {
      console.log(`[ISOLATED_LOADING] Feature 1 SHOULD be loading County data!`);
    } else {
      console.log(`[ISOLATED_LOADING] Feature 1 should NOT load - conditions not met`);
    }
  }, [shouldLoadFeature1, areaType, view]);

  // FEATURE 2: Best Performing Loader (Isolated) - Only for Feature 2
  const shouldLoadFeature2 = areaType !== "" && (view === "performance" || view === "performance-snapshot");
  console.log(`[ISOLATED_LOADING] Feature 2 - shouldLoad: ${shouldLoadFeature2}, view: "${view}", areaType: "${areaType}"`);
  
  const feature2Loader = useFeature2DataLoader({
    areaType,
    setFeature2Data,
    setFeature2Loading,
    setFeature2Error,
    shouldLoad: shouldLoadFeature2
  });

  // FEATURE 2: Load Best Performing Linked files for Explore dropdowns
  useEffect(() => {
    if (!shouldLoadFeature2) return;
    let cancelled = false;

    const loadLinked = async () => {
      try {
        const base = 'public/data/Best Performing Linked';
        const files = [
          { level: 'State', key: `${base}/best_performing_rank_state_linked.csv` },
          { level: 'Metro', key: `${base}/best_performing_rank_metro_linked.csv` },
          { level: 'County', key: `${base}/best_performing_rank_county_linked.csv` },
          { level: 'City', key: `${base}/best_performing_rank_city_linked.csv` },
          { level: 'Zipcode', key: `${base}/best_performing_rank_zipcode_linked.csv` },
        ];

        const results = await Promise.all(files.map(async ({ level, key }) => {
          try {
            const text = await fetchR2CsvText(key);
            const map = {};
            const mapById = {};
            Papa.parse(text, {
              header: true,
              dynamicTyping: true,
              skipEmptyLines: 'greedy',
              transformHeader: (h) => String(h || '').trim(),
              complete: (result) => {
                for (const r of (result.data || [])) {
                  // Column names in enhanced files
                  const key = String(r.AreaName || r.State || r.Metro || r.CountyName || r.City || r.Zipcode || r.Neighborhood || '').trim();
                  if (!key) continue;
                  const parentRegionId = Number(r.RegionID) || Number(r['State RegionID']) || Number(r['Metro RegionID']) || Number(r['County RegionID']) || Number(r['City RegionID']) || Number(r['Zipcode RegionID']) || Number(r['Neighborhood RegionID']) || null;
                  const entry = {
                    BestMetro: String(r.Metro || r.BestMetro || ''),
                    BestCounty: String(r.CountyName || r.BestCounty || ''),
                    BestCity: String(r.City || r.BestCity || ''),
                    BestZipcode: String(r.Zipcode || r.BestZipcode || ''),
                    BestNeighborhood: String(r.Neighborhood || r.BestNeighborhood || ''),
                    ST: String(r.State || r.ST || ''),
                    // RegionIDs for robust matching
                    BestMetroId: Number(r['Metro RegionID']) || null,
                    BestCountyId: Number(r['County RegionID']) || null,
                    BestCityId: Number(r['City RegionID']) || null,
                    BestZipcodeId: Number(r['Zipcode RegionID']) || null,
                    BestNeighborhoodId: Number(r['Neighborhood RegionID']) || null,
                  };
                  map[key] = entry;
                  if (parentRegionId != null) {
                    mapById[parentRegionId] = entry;
                  }
                }
              },
            });
            return { level, map, mapById };
          } catch (_) {
            return { level, map: null, mapById: null };
          }
        }));

        if (cancelled) return;
        const next = {};
        const nextById = {};
        for (const { level, map, mapById } of results) {
          if (map && Object.keys(map).length) next[level] = map;
          if (mapById && Object.keys(mapById).length) nextById[level] = mapById;
        }
        if (Object.keys(next).length) setLinksByName(next);
        if (Object.keys(nextById).length) setLinksByRegionId(nextById);
      } catch (_) {}
    };

    loadLinked();
    return () => { cancelled = true; };
  }, [shouldLoadFeature2, setLinksByName, setLinksByRegionId]);

  // FEATURE 4: New Markets TP Loader (Isolated) - Only for Feature 4
  const shouldLoadFeature4 = areaType !== "" && (view === "newMarketsTP" || view === "newMarketsTP-snapshot");
  console.log(`[ISOLATED_LOADING] Feature 4 - shouldLoad: ${shouldLoadFeature4}, view: "${view}", areaType: "${areaType}"`);
  
  const feature4Loader = useFeature4DataLoader({
    areaType,
    selectedName,
    setFeature4Data,
    setFeature4Loading,
    setFeature4Error,
    shouldLoad: shouldLoadFeature4
  });

  // UPDATE LOADING STATE BASED ON CURRENT FEATURE
  useEffect(() => {
    let currentLoading = false;
    let currentError = null;

    switch (view) {
      case "metro":
      case "direct":
      case "metro-snapshot":
        currentLoading = feature1Loading;
        currentError = feature1Error;
        break;
      case "performance":
      case "performance-snapshot":
        currentLoading = feature2Loading;
        currentError = feature2Error;
        break;
      case "newMarketsTP":
      case "newMarketsTP-snapshot":
        currentLoading = feature4Loading;
        currentError = feature4Error;
        break;
      default:
        // When view is empty or unknown, default to Feature 1 (metro view)
        currentLoading = feature1Loading;
        currentError = feature1Error;
    }

    setIsLoading(currentLoading);
    
    if (currentError) {
      console.error(`[ISOLATED_LOADING] Feature ${view} error:`, currentError);
    }
  }, [view, feature1Loading, feature1Error, feature2Loading, feature2Error, feature4Loading, feature4Error, setIsLoading]);

  // LOAD CHART DATA ON DEMAND
  const loadChartData = async (metric, target, regionId = null) => {
    console.log(`[ISOLATED_LOADING] loadChartData called with metric: ${metric}, target: ${target}, regionId: ${regionId}`);
    console.log(`[ISOLATED_LOADING] Current view: ${view}, areaType: ${areaType}`);
    console.log(`[ISOLATED_LOADING] Feature loaders available:`, {
      feature1Loader: !!feature1Loader,
      feature2Loader: !!feature2Loader,
    });
    
    if (!areaType || !target) {
      console.log(`[ISOLATED_LOADING] Missing requirements - areaType: ${areaType}, target: ${target}`);
      return;
    }

    // Set loading state for Zipcode and Neighborhood
    if ((areaType === 'Zipcode' || areaType === 'Neighborhood') && setIsLoading) {
      setIsLoading(true);
      console.log(`[ISOLATED_LOADING] Set loading state TRUE for ${areaType} chart loading`);
    }

    setCurrentChartMetric(metric);
    setSelectedMetro(target);
    setSelectedName(target);

    try {
      switch (view) {
        case "":
          // Treat empty view like Feature 1 (direct selection)
          console.log(`[ISOLATED_LOADING] (empty view) Calling Feature 1 chart loading for ${target}, metric: ${metric}, regionId: ${regionId}`);
          if (feature1Loader.loadChartData) {
            await feature1Loader.loadChartData(target, metric, regionId);
            console.log(`[ISOLATED_LOADING] Feature 1 chart loading completed`);
          }
          break;
        case "metro":
        case "direct":
        case "metro-snapshot":
          // Feature 1 chart loading - call the loader's loadChartData function
          console.log(`[ISOLATED_LOADING] Calling Feature 1 chart loading for ${target}, metric: ${metric}, regionId: ${regionId}`);
          if (feature1Loader.loadChartData) {
            console.log(`[ISOLATED_LOADING] Feature 1 loader has loadChartData function, calling it...`);
            await feature1Loader.loadChartData(target, metric, regionId);
            console.log(`[ISOLATED_LOADING] Feature 1 chart loading completed`);
          } else {
            console.log(`[ISOLATED_LOADING] Feature 1 loader does not have loadChartData function`);
          }
          break;
        case "performance":
        case "performance-snapshot":
          if (feature2Loader.loadChartData) {
            console.log(`[ISOLATED_LOADING] Calling Feature 2 chart loading for ${target}, metric: ${metric}, regionId: ${regionId}`);
            await feature2Loader.loadChartData(target, metric, regionId);
            console.log(`[ISOLATED_LOADING] Feature 2 chart loading completed`);
          }
          break;
        case "newMarketsTP":
        case "newMarketsTP-snapshot":
          if (feature4Loader.loadChartData) {
            console.log(`[ISOLATED_LOADING] Calling Feature 4 chart loading for ${target}, metric: ${metric}, regionId: ${regionId}`);
            await feature4Loader.loadChartData(target, metric, regionId);
            console.log(`[ISOLATED_LOADING] Feature 4 chart loading completed`);
          }
          break;
        default:
          // No fallback - each feature should be explicitly handled
          console.log(`[ISOLATED_LOADING] No chart loading handler for view: ${view}`);
          break;
      }
    } finally {
      // Clear loading state for Zipcode and Neighborhood
      if ((areaType === 'Zipcode' || areaType === 'Neighborhood') && setIsLoading) {
        // Small timeout to ensure UI updates
        setTimeout(() => {
          setIsLoading(false);
          console.log(`[ISOLATED_LOADING] Set loading state FALSE for ${areaType} chart loading`);
        }, 100);
      }
    }
  };

  // RELOAD CURRENT FEATURE
  const reloadCurrentFeature = () => {
    console.log(`[ISOLATED_LOADING] Reloading current feature: ${view}`);
    
    switch (view) {
      case "metro":
      case "direct":
      case "metro-snapshot":
        if (feature1Loader.reloadFeature1) {
          feature1Loader.reloadFeature1();
        }
        break;
      case "performance":
      case "performance-snapshot":
        if (feature2Loader.reloadFeature2) {
          feature2Loader.reloadFeature2();
        }
        break;
      default:
        // No fallback - each feature should be explicitly handled
        console.log(`[ISOLATED_LOADING] No reload handler for view: ${view}`);
    }
  };

  // CLEAR ALL CACHES
  const clearAllCaches = () => {
    console.log(`[ISOLATED_LOADING] Clearing all feature caches`);
    
    if (feature1Loader.clearFeature1Cache) {
      feature1Loader.clearFeature1Cache();
    }
    if (feature2Loader.clearFeature2Cache) {
      feature2Loader.clearFeature2Cache();
    }
  };

  // GET CACHE STATS
  const getCacheStats = () => {
    const stats = {
      feature1: feature1Loader.getFeature1CacheStats ? feature1Loader.getFeature1CacheStats() : null,
      feature2: feature2Loader.getFeature2CacheStats ? feature2Loader.getFeature2CacheStats() : null
    };
    
    console.log(`[ISOLATED_LOADING] Cache stats:`, stats);
    return stats;
  };

  // Debug Feature 1 data after loading
  useEffect(() => {
    console.log(`[ISOLATED_LOADING] Feature 1 data check:`, {
      areaType,
      view,
      shouldLoadFeature1,
      feature1Data: {
        allDates: feature1Data.allDates?.length || 0,
        latestByMetroKeys: Object.keys(feature1Data.latestByMetro || {}),
        latestDate: feature1Data.latestDate,
        chartData: feature1Data.chartData?.length || 0
      },
      feature1Loading,
      feature1Error
    });
    
    // Show key information directly
    console.log(`[ISOLATED_LOADING] Feature 1 - shouldLoad: ${shouldLoadFeature1}`);
    console.log(`[ISOLATED_LOADING] Feature 1 - latestByMetro keys:`, Object.keys(feature1Data.latestByMetro || {}));
    console.log(`[ISOLATED_LOADING] Feature 1 - latestByMetro empty?`, Object.keys(feature1Data.latestByMetro || {}).length === 0);
  }, [areaType, view, shouldLoadFeature1, feature1Data, feature1Loading, feature1Error]);

  // RETURN ISOLATED LOADING UTILITIES
  return {
    loadChartData,
    reloadCurrentFeature,
    clearAllCaches,
    getCacheStats,
    
    // Feature-specific loaders (for advanced usage)
    feature1Loader,
    feature2Loader,
    feature4Loader
  };
}
